// Single source of truth for the client-rendered `.card` markup, shared by the
// homepage state panel and the bookmarks menu. Mirrors
// templates/partials/card.html.twig — keep the two in sync.

import { esc } from './utils';

export interface CardData {
  type: 'theme' | 'perspective';
  /** Vimeo id (or node fallback). Sets data-video-id; omitted when empty. */
  videoId: string;
  title: string;
  state: string;
  color: string;
  url: string;
  thumbnail: string;
  /** Width-descriptor srcset for the thumbnail; empty when unavailable. */
  srcset?: string;
  /** `sizes` attribute paired with `srcset`. */
  sizes?: string;
  duration: string;
}

const STAR_SVG = `<svg class="card__label-star" viewBox="0 0 100 100" aria-hidden="true" focusable="false" width="12" height="12"><path d="M50 5L61 35H93L68 54L77 84L50 65L23 84L32 54L7 35H39Z" fill="currentColor"/></svg>`;

/** Builds the `.card` HTML for one video. All dynamic values are escaped. */
export function buildCard(card: CardData): string {
  // Theme cards use a slightly larger play glyph than perspective cards.
  const playSize = card.type === 'theme' ? 10 : 8;
  const videoIdAttr = card.videoId ? ` data-video-id="${esc(card.videoId)}"` : '';
  const responsiveAttrs = card.srcset
    ? ` srcset="${esc(card.srcset)}" sizes="${esc(card.sizes ?? '')}"`
    : '';
  const image = card.thumbnail
    ? `<div class="card__image"><img src="${esc(card.thumbnail)}"${responsiveAttrs} alt="" aria-hidden="true" loading="lazy" decoding="async"></div>`
    : '';

  return `
    <div class="card card--${card.type}"${videoIdAttr} style="--state-color: ${esc(card.color)}">
      ${image}
      <div class="card__content repel">
        <div class="card__copy flow">
          <div class="card__label cluster">
            ${STAR_SVG}
            <span>${esc(card.state)}</span>
          </div>
          <h3 class="card__title">
            <a class="card__link cover-link" href="${esc(card.url)}">${esc(card.title)}</a>
          </h3>
        </div>
        <div class="card__play-wrap" aria-hidden="true">
          <span class="card__duration">${esc(card.duration)}</span>
          <span class="card__watched-label">Just watched</span>
          <span class="card__play">
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="${playSize}" height="${playSize}"><path d="M4 3l10 5-10 5V3z" fill="currentColor"/></svg>
          </span>
        </div>
      </div>
    </div>
  `;
}
